import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import s from './HeaderBio.module.css'

function cleanBio(bio) {
  if (bio === null) {
    return ''
  }

  const cleanedBio = bio.replace(/<p>/g, '')
  const cleanedBioParts = cleanedBio.split(/<\/p>?\r*\n*/)

  return cleanedBioParts
    .filter((line) => line !== '' && line !== '&nbsp;')
    .map((line) => `<p>${line}</p>\n`)
    .join('')
}

function isBioEmpty(bio) {
  return bio === null || bio === ''
}

function HeaderBio({
  bio = '',
  minHeight = 100,
  imgHalfRef = null,
  titleContainerRef = null,
}) {
  const cleanedBio = useMemo(() => cleanBio(bio), [bio])

  const bioTextRef = useRef()
  const bioContainerRef = useRef()

  const [isExpandable, setIsExpandable] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [height, setHeight] = useState(minHeight)
  const [maxHeight, setMaxHeight] = useState(400)

  useEffect(() => {
    const handleResize = () => {
      // bio is empty in this render -- nothing to measure. bioTextRef.current
      // is genuinely null in that case (its element isn't rendered below),
      // unlike imgHalfRef/titleContainerRef which ModelHeader always renders.
      if (
        !imgHalfRef?.current ||
        !titleContainerRef?.current ||
        !bioTextRef.current
      ) {
        return
      }

      const paddingHeight = 30
      const readMoreHeight = 16
      const imgHeight = imgHalfRef.current.clientHeight
      const titleContainerHeight = titleContainerRef.current.clientHeight
      const bioTextHeight = bioTextRef.current.clientHeight

      const bioContentHeight =
        imgHeight - titleContainerHeight - readMoreHeight - paddingHeight * 2

      setIsExpandable(bioTextHeight > minHeight)

      if (isExpanded) {
        setHeight(bioContentHeight)
      }
      setMaxHeight(bioContentHeight)
    }

    window.addEventListener('resize', handleResize)

    // Call the function initially to set the state based on the initial size
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [
    imgHalfRef,
    titleContainerRef,
    bioTextRef,
    isExpanded,
    minHeight,
    cleanedBio,
  ])

  if (isBioEmpty(cleanedBio)) {
    return <></>
  }

  const handleClick = () => {
    const nowIsExpanded = !isExpanded

    setHeight(nowIsExpanded ? maxHeight : minHeight)
    setIsExpanded(nowIsExpanded)

    if (bioContainerRef.current) {
      bioContainerRef.current.scrollTop = 0
    }
  }

  return (
    <div className={s.wrapper}>
      <div
        style={{ height, overflow: isExpanded ? 'scroll' : 'hidden' }}
        className={s.container}
        ref={bioContainerRef}
      >
        <div
          dangerouslySetInnerHTML={{ __html: cleanedBio }}
          ref={bioTextRef}
        />
      </div>
      {isExpandable && (
        <button className={s.collapse} onClick={handleClick}>
          {isExpanded ? 'Collapse' : 'Read More'}
          <span aria-hidden={true} className={s.expander}>
            {isExpanded ? '-' : '+'}
          </span>
        </button>
      )}
    </div>
  )
}

HeaderBio.propTypes = {
  bio: PropTypes.string,
  minHeight: PropTypes.number,
  imgHalfRef: PropTypes.object,
  titleContainerRef: PropTypes.object,
}

export default HeaderBio
